import React from 'react';
import { Pill, Calendar, Activity, TrendingUp, History, Plus, LogIn, LogOut, ShieldAlert, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { APP_VERSION, APP_NAME } from '../utils/version';

export default function Navbar({ 
  activeTab = 'stack',
  onSelectTab = () => {},
  onOpenAuth, 
  onOpenAddModal, 
  onOpenHistoryModal, 
  onOpenReleaseNotes,
  onTriggerSeed 
}) {
  const { currentUser, isDemoMode, isFirebaseConfigured, logout } = useAuth();

  const handleNavClick = (e, tab) => {
    // Allow modifier keys or middle-click to open naturally in a new tab
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
      return;
    }
    e.preventDefault();
    onSelectTab(tab);
  };

  const renderNavSegment = (isMobile = false) => (
    <nav className={`items-center bg-slate-900/90 p-1 rounded-xl border border-slate-800 shadow-inner ${
      isMobile ? 'flex w-full justify-between sm:hidden' : 'hidden sm:inline-flex shrink-0'
    }`}>
      <a
        href="/stack"
        onClick={(e) => handleNavClick(e, 'stack')}
        className={`flex items-center justify-center space-x-1.5 ${isMobile ? 'flex-1 px-2 py-1.5' : 'px-3 py-1.5'} rounded-lg text-xs font-semibold transition ${
          activeTab === 'stack'
            ? 'bg-sky-500 text-white shadow-sm'
            : 'text-slate-400 hover:text-slate-200'
        }`}
      >
        <Pill className="w-3.5 h-3.5" />
        <span>Stack</span>
      </a>

      <a
        href="/tools"
        onClick={(e) => handleNavClick(e, 'tools')}
        className={`flex items-center justify-center space-x-1.5 ${isMobile ? 'flex-1 px-2 py-1.5' : 'px-3 py-1.5'} rounded-lg text-xs font-semibold transition ${
          activeTab === 'tools'
            ? 'bg-sky-500 text-white shadow-sm'
            : 'text-slate-400 hover:text-slate-200'
        }`}
      >
        <Activity className="w-3.5 h-3.5" />
        <span>Tools</span>
      </a>

      <a
        href="/serum"
        onClick={(e) => handleNavClick(e, 'serum')}
        className={`flex items-center justify-center space-x-1.5 ${isMobile ? 'flex-1 px-2 py-1.5' : 'px-3 py-1.5'} rounded-lg text-xs font-semibold transition ${
          activeTab === 'serum'
            ? 'bg-sky-500 text-white shadow-sm'
            : 'text-slate-400 hover:text-slate-200'
        }`}
      >
        <TrendingUp className="w-3.5 h-3.5" />
        <span>Serum</span>
        <span className={`px-1 py-0.2 rounded-full text-[9px] font-bold ${
          activeTab === 'serum' ? 'bg-white/25 text-white' : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
        }`}>
          New
        </span>
      </a>

      <a
        href="/calendar"
        onClick={(e) => handleNavClick(e, 'calendar')}
        className={`flex items-center justify-center space-x-1.5 ${isMobile ? 'flex-1 px-2 py-1.5' : 'px-3 py-1.5'} rounded-lg text-xs font-semibold transition ${
          activeTab === 'calendar'
            ? 'bg-sky-500 text-white shadow-sm'
            : 'text-slate-400 hover:text-slate-200'
        }`}
      >
        <Calendar className="w-3.5 h-3.5" />
        <span>Calendar</span>
      </a>
    </nav>
  );

  return (
    <header className="sticky top-0 z-30 glass-panel border-b border-slate-800/80 px-3 sm:px-4 lg:px-8 py-2.5 sm:py-3">
      <div className="max-w-7xl mx-auto flex flex-col gap-2.5 sm:gap-3 sm:flex-row sm:items-center sm:justify-between">
        
        {/* Brand & Desktop Navigation Tabs */}
        <div className="flex items-center justify-between sm:justify-start sm:space-x-6 w-full sm:w-auto">
          <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto sm:space-x-4">
            <a
              href="/stack"
              onClick={(e) => handleNavClick(e, 'stack')}
              className="flex items-center space-x-2.5 sm:space-x-3 cursor-pointer group min-w-0 mr-2 sm:mr-0"
              title="Go to Protocol Stack"
            >
              <img 
                src="/favicon.png" 
                alt="Supps & Peps Logo" 
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl shadow-lg shadow-sky-500/20 border border-sky-500/30 object-cover shrink-0 group-hover:border-sky-400 transition" 
              />
              <div className="min-w-0">
                <h1 className="font-bold text-sm sm:text-lg lg:text-xl tracking-tight text-white leading-tight group-hover:text-sky-300 transition truncate">
                  <span className="hidden sm:inline">{APP_NAME}</span>
                  <span className="sm:hidden">Supps & Peps</span>
                </h1>
                <p className="text-xs text-slate-400 font-medium hidden sm:block">
                  Supplement & Peptide Protocol Management
                </p>
              </div>
            </a>

            <div className="flex items-center space-x-1.5 shrink-0">
              <button
                type="button"
                onClick={onOpenReleaseNotes}
                className="group inline-flex items-center space-x-1 px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold bg-sky-500/10 hover:bg-sky-500/20 text-sky-400 hover:text-sky-300 border border-sky-500/20 hover:border-sky-400/40 transition shadow-sm cursor-pointer focus:outline-none focus:ring-1 focus:ring-sky-400/50"
                title="View Release Notes & Version Updates"
                aria-label="View Release Notes"
              >
                <Sparkles className="w-3 h-3 text-sky-400 group-hover:rotate-12 transition-transform duration-200" />
                <span>v{APP_VERSION}</span>
              </button>
              {currentUser ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    logout();
                  }}
                  className="sm:hidden p-1.5 rounded-lg bg-slate-800/90 hover:bg-red-500/20 text-slate-400 hover:text-red-400 border border-slate-700 transition"
                  title="Sign Out"
                  aria-label="Sign Out"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onOpenAuth}
                  className="sm:hidden flex items-center space-x-1 px-2.5 py-1 rounded-lg bg-sky-500 hover:bg-sky-400 text-white text-xs font-semibold transition shadow-sm whitespace-nowrap"
                  title="Sign In"
                  aria-label="Sign In"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  <span>Sign In</span>
                </button>
              )}
            </div>
          </div>

          {/* Navigation Segment Tabs - Desktop */}
          {renderNavSegment(false)}
        </div>

        {/* Mobile Navigation Tabs */}
        <div className="w-full sm:hidden">
          {renderNavSegment(true)}
        </div>

        {/* Dedicated Mobile Action Row */}
        {currentUser && (
          <div className="flex sm:hidden items-center gap-2 w-full">
            <button
              onClick={onOpenAddModal}
              className="flex-1 flex items-center justify-center space-x-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-sky-500 via-sky-600 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 text-white text-xs font-bold shadow-lg shadow-sky-500/25 active:scale-[0.98] transition"
            >
              <Plus className="w-4 h-4 stroke-[2.5]" />
              <span>Add Protocol</span>
            </button>
            <button
              onClick={() => onOpenHistoryModal()}
              className="flex items-center justify-center p-2.5 rounded-xl bg-slate-800/90 hover:bg-slate-700 text-slate-200 border border-slate-700 transition shrink-0 active:scale-95 shadow-sm"
              title="View Change History"
              aria-label="View Change History"
            >
              <History className="w-4 h-4 text-sky-400" />
            </button>
          </div>
        )}

        {/* Action Controls - Desktop */}
        <div className="hidden sm:flex items-center justify-end space-x-3 w-auto shrink-0">
          {!isFirebaseConfigured && (
            <div className="hidden md:flex items-center space-x-1.5 px-3 py-1 rounded-lg bg-amber-500/10 text-amber-400 text-xs border border-amber-500/20">
              <ShieldAlert className="w-3.5 h-3.5" />
              <span>Local Storage Mode</span>
            </div>
          )}

          {currentUser && (
            <>
              <button
                onClick={() => onOpenHistoryModal()}
                className="flex items-center space-x-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-medium border border-slate-700 transition"
                title="View Change History"
              >
                <History className="w-4 h-4 text-sky-400" />
                <span className="hidden sm:inline">Audit Log</span>
              </button>

              <button
                onClick={onOpenAddModal}
                className="flex items-center space-x-1.5 px-3 sm:px-3.5 py-1.5 sm:py-2 rounded-lg bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 text-white text-xs font-semibold shadow-md shadow-sky-500/20 transition whitespace-nowrap"
              >
                <Plus className="w-4 h-4 shrink-0" />
                <span>Add Protocol</span>
              </button>
            </>
          )}

          {/* User Auth Info / Login Logout */}
          {currentUser ? (
            <div className="flex items-center space-x-2 pl-2 border-l border-slate-800">
              <div className="hidden lg:block text-right">
                <div className="text-xs font-medium text-slate-200 truncate max-w-[140px]">
                  {currentUser.displayName || currentUser.email}
                </div>
                <div className="text-[10px] text-sky-400 font-mono">
                  {isDemoMode ? 'Demo Session' : 'Firebase Auth'}
                </div>
              </div>
              <button
                onClick={logout}
                className="p-1.5 sm:p-2 rounded-lg bg-slate-800 hover:bg-red-500/20 text-slate-300 hover:text-red-400 border border-slate-700 transition"
                title="Sign Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={onOpenAuth}
              className="flex items-center space-x-1.5 px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-sky-500 hover:bg-sky-400 text-white text-xs font-semibold transition shadow-md shadow-sky-500/20 whitespace-nowrap"
            >
              <LogIn className="w-4 h-4" />
              <span>Sign In</span>
            </button>
          )}
        </div>

      </div>
    </header>
  );
}
